import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { Button, Field } from "../components/UIKit";
import { api } from "../api/client";

// Two-step deletion:
//   step=1 → strong-intent warning + type "SUPPRIMER"
//   step=2 → re-authenticate (password OR reuse existing Google flow)
export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, signInWithGoogle, signOut } = useAuth();

  const [step, setStep] = useState(1);
  const [confirmation, setConfirmation] = useState("");
  const [password, setPassword] = useState("");
  const [googleSessionId, setGoogleSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The current user's credential profile (email/password, Google, both).
  // We infer from the presence of email + the AuthContext user shape. The
  // backend still does the source-of-truth check, but we use this to guide
  // the UI (show password field vs Google re-auth text button).
  const emailAccount = !!user?.email;

  // Consume any Google session_id stashed by AuthContext during the
  // redirect-back handshake. sessionStorage only — never localStorage.
  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("mf_delete_google_session_id");
      if (stored) {
        setGoogleSessionId(stored);
        // Auto-advance to step 2 so the user immediately sees the ready state.
        setStep(2);
      }
    } catch { /* ignore */ }
  }, []);

  // Wipe the transient google_session_id whenever we leave this page or
  // finalize the flow. Never persist across sessions.
  const wipeTransientGoogle = () => {
    try {
      window.sessionStorage.removeItem("mf_delete_google_session_id");
      window.sessionStorage.removeItem("mf_delete_reauth");
    } catch { /* ignore */ }
  };
  useEffect(() => () => wipeTransientGoogle(), []);

  const confirmationValid = useMemo(() => confirmation === "SUPPRIMER", [confirmation]);

  const onCancel = () => {
    wipeTransientGoogle();
    navigate("/profile", { replace: true });
  };

  const onStartGoogleReauth = () => {
    // Arm the interception flag so AuthContext knows the returning
    // #session_id is for deletion (NOT login / linking).
    try {
      window.sessionStorage.setItem("mf_delete_reauth", "1");
    } catch { /* ignore */ }
    signInWithGoogle();
  };

  const onSubmit = async () => {
    if (busy) return;
    setError(null);
    if (!confirmationValid) {
      setError("Tapez exactement SUPPRIMER pour confirmer.");
      return;
    }
    if (!password && !googleSessionId) {
      setError("Réauthentification requise (mot de passe ou Google).");
      return;
    }
    setBusy(true);
    try {
      const payload = { confirmation: "SUPPRIMER" };
      if (password) payload.password = password;
      if (googleSessionId) payload.google_session_id = googleSessionId;
      await api.delete("/auth/account", { data: payload });
      // Success — wipe local session and hop to /login?deleted=1.
      wipeTransientGoogle();
      await signOut();
      window.location.replace("/login?deleted=1");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) setError(detail || "Réauthentification invalide.");
      else if (status === 422) setError(detail || "Confirmation invalide.");
      else if (status === 429) setError("Trop de tentatives. Réessayez plus tard.");
      else setError(detail || "Suppression impossible. Réessayez.");
      // If the Google session was rejected, clear it so the user can retry.
      if (status === 401 && googleSessionId) {
        setGoogleSessionId(null);
        try { window.sessionStorage.removeItem("mf_delete_google_session_id"); } catch { /* ignore */ }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <PageHeader title="Supprimer mon compte" />
      <div style={{ padding: "0 20px 100px" }}>
        {/* Warning card — always visible */}
        <div
          data-testid="delete-account-warning-card"
          className="mf-anim-in"
          style={{
            background: "var(--mf-bg-card)",
            borderRadius: 20,
            padding: 24,
            boxShadow: "var(--mf-shadow)",
            border: "1px solid rgba(220, 38, 38, 0.15)",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <span style={{
              width: 40, height: 40, borderRadius: 12,
              background: "rgba(220, 38, 38, 0.10)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
              <AlertTriangle size={22} color="#dc2626" />
            </span>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.3 }}>
              Action irréversible
            </h2>
          </div>
          <p style={{ margin: "0 0 8px", fontSize: 15, color: "var(--mf-text-secondary)", lineHeight: 1.55 }}>
            La suppression de votre compte est <strong>immédiate et définitive</strong>. Elle entraîne :
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, color: "var(--mf-text-secondary)", fontSize: 14, lineHeight: 1.7 }}>
            <li>La suppression de tous vos véhicules et leurs entretiens</li>
            <li>La révocation de vos partages actifs</li>
            <li>La désinscription de toutes vos notifications push</li>
            <li>La perte définitive de votre historique et de vos documents</li>
          </ul>
          <p style={{ margin: "12px 0 0", fontSize: 13, color: "var(--mf-text-muted)", lineHeight: 1.5 }}>
            Aucune sauvegarde ne sera conservée après cette opération.
          </p>
        </div>

        {step === 1 && (
          <div
            data-testid="delete-account-step1"
            className="mf-anim-in"
            style={{
              background: "var(--mf-bg-card)",
              borderRadius: 20,
              padding: 24,
              boxShadow: "var(--mf-shadow)",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 16, fontWeight: 700, color: "var(--mf-text)" }}>
              Étape 1 / 2 — Confirmer l&apos;intention
            </h3>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: "var(--mf-text-secondary)", lineHeight: 1.5 }}>
              Pour continuer, tapez le mot <code style={{
                background: "rgba(11,25,44,0.06)", padding: "2px 8px", borderRadius: 6,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 700,
              }}>SUPPRIMER</code> en majuscules exactement.
            </p>
            <Field
              testID="delete-account-confirm-input"
              label="Confirmation"
              value={confirmation}
              onChange={setConfirmation}
              placeholder="SUPPRIMER"
              autoComplete="off"
            />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <Button
                testID="delete-account-cancel-1"
                variant="secondary"
                fullWidth
                onClick={onCancel}
                icon={<ArrowLeft size={16} />}
              >
                Annuler
              </Button>
              <Button
                testID="delete-account-next-btn"
                fullWidth
                onClick={() => setStep(2)}
                disabled={!confirmationValid}
              >
                Continuer
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div
            data-testid="delete-account-step2"
            className="mf-anim-in"
            style={{
              background: "var(--mf-bg-card)",
              borderRadius: 20,
              padding: 24,
              boxShadow: "var(--mf-shadow)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <ShieldAlert size={18} color="var(--mf-brand)" />
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--mf-text)" }}>
                Étape 2 / 2 — Réauthentification
              </h3>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: "var(--mf-text-secondary)", lineHeight: 1.5 }}>
              {emailAccount
                ? "Saisissez votre mot de passe pour confirmer, ou reconfirmez avec Google si votre compte est lié."
                : "Reconfirmez votre identité pour finaliser la suppression."}
            </p>

            <Field
              testID="delete-account-password-input"
              label="Mot de passe (comptes email)"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="Laissez vide si compte Google uniquement"
              autoComplete="current-password"
            />

            <div style={{
              margin: "6px 0 14px",
              padding: "12px 14px",
              background: "rgba(11,25,44,0.04)",
              borderRadius: 12,
              fontSize: 13,
              color: "var(--mf-text-secondary)",
              lineHeight: 1.5,
            }}>
              {googleSessionId ? (
                <span data-testid="delete-account-google-ready">
                  ✓ Session Google vérifiée pour la suppression.
                </span>
              ) : (
                <>
                  Vous préférez confirmer avec Google ?{" "}
                  <button
                    type="button"
                    onClick={onStartGoogleReauth}
                    data-testid="delete-account-google-reauth-btn"
                    style={{
                      background: "transparent", border: "none",
                      color: "var(--mf-brand)", cursor: "pointer",
                      fontWeight: 700, padding: 0, textDecoration: "underline",
                    }}
                  >
                    Confirmer avec Google
                  </button>
                </>
              )}
            </div>

            {error && (
              <div data-testid="delete-account-error" style={{
                marginBottom: 12, padding: "10px 12px", borderRadius: 10,
                background: "rgba(220, 38, 38, 0.08)", color: "#b91c1c",
                fontSize: 13, fontWeight: 500,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <Button
                testID="delete-account-back-btn"
                variant="secondary"
                fullWidth
                onClick={() => { setStep(1); setError(null); }}
                icon={<ArrowLeft size={16} />}
                disabled={busy}
              >
                Retour
              </Button>
              <Button
                testID="delete-account-confirm-btn"
                fullWidth
                onClick={onSubmit}
                loading={busy}
                disabled={busy || (!password && !googleSessionId)}
              >
                Supprimer définitivement
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
